import React, { useEffect, useState } from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FrameCard from "../../shared/FrameCard";
import EvaluationStandardsSection from "../EvaluationStandardsSection";
import SkillCard from "../SkillCard";
import ActionButton from "../../shared/ActionButton";
import { PROJECT_COLOR, PROJECT_COLOR_DARK } from "../../../constants/colors";
import { toast } from "react-toastify";

export default function SelfEvaluationsEdit() {
  const [skillCategories, setSkillCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // データ取得
  useEffect(() => {
    fetch("/skill_sheet_self_evaluations/edit.json")
      .then((response) => response.json())
      .then((data) => {
        setSkillCategories(data.skillCategories);
        setLoading(false);
      })
      .catch((error) => {
        console.error("API error", error);
      });
  }, []);

  // 親スキル変更
  function handleParentLevelChange(categoryKey, groupId, newLevelValue) {
    setSkillCategories((previousCategories) =>
      previousCategories.map((category) => {
        if (category.categoryKey !== categoryKey) return category;

        return {
          ...category,
          skillGroups: category.skillGroups.map((group) => {
            if (group.groupId !== groupId) return group;

            return {
              ...group,
              parentSkill: {
                ...group.parentSkill,
                levelValue: newLevelValue,
              },
            };
          }),
        };
      })
    );
  }

  // 子スキル変更
  function handleChildLevelChange(categoryKey, groupId, recordId, newLevelValue) {
    setSkillCategories((previousCategories) =>
      previousCategories.map((category) => {
        if (category.categoryKey !== categoryKey) return category;

        return {
          ...category,
          skillGroups: category.skillGroups.map((group) => {
            if (group.groupId !== groupId) return group;

            return {
              ...group,
              childSkills: group.childSkills.map((child) => {
                if (child.recordId !== recordId) return child;
                return { ...child, levelValue: newLevelValue };
              }),
            };
          }),
        };
      })
    );
  }

  if (loading) return <div>Loading...</div>;

  function levelBeforeTypeCastFromLevelValue(levelValue) {
    const normalized = Number.isFinite(levelValue) ? levelValue : 25;
    const clamped = Math.min(100, Math.max(0, normalized));
    return Math.round(clamped / 25) + 1; // 0→1, 25→2, ... 100→5
  }
  
  function buildSkillSheetTemplateAttributesFromSkillCategories(skillCategories) {
    const attributes = {
      skill_sheet_language_infos_attributes: {},
      skill_sheet_business_system_infos_attributes: {},
      skill_sheet_cloud_services_attributes: {},
      skill_sheet_server_operating_system_infos_attributes: {},
      skill_sheet_middleware_infos_attributes: {},
      skill_sheet_operating_system_infos_attributes: {},
      skill_sheet_db_infos_attributes: {},
      skill_sheet_network_infos_attributes: {},
      skill_sheet_design_tool_infos_attributes: {},
    };
  
    function setAttributes(attributeKey, recordId, levelKeyName, levelValue) {
      if (!recordId) return;
      attributes[attributeKey][String(recordId)] = {
        id: recordId,
        [levelKeyName]: levelBeforeTypeCastFromLevelValue(levelValue),
      };
    }
  
    for (const category of skillCategories) {
      for (const skillGroup of category.skillGroups) {
        const parentSkill = skillGroup.parentSkill;
        const recordId = parentSkill?.recordId;
        const levelValue = parentSkill?.levelValue;
  
        switch (category.categoryKey) {
          case "front_end":
          case "back_end": {
            if (!recordId) break;

            // 言語
            upsertLanguageInfoAttributes(attributes, recordId, {
              language_level: levelBeforeTypeCastFromLevelValue(levelValue),
            });

            // fw/ライブラリ
            for (const childSkill of skillGroup.childSkills || []) {
              if (!childSkill?.recordId) continue;

              upsertLanguageInfoAttributes(attributes, childSkill.recordId, {
                fw_library_level: levelBeforeTypeCastFromLevelValue(childSkill.levelValue),
              });
            }

            break;
          }
  
          case "business_system":
            setAttributes(
              "skill_sheet_business_system_infos_attributes",
              recordId,
              "business_system_level",
              levelValue
            );
            break;
  
            case "cloud": {
              if (!recordId) break;
              const levelBeforeTypeCast = levelBeforeTypeCastFromLevelValue(levelValue);
              attributes.skill_sheet_cloud_services_attributes[String(recordId)] = {
                id: recordId,
                cloud_service_level: levelBeforeTypeCast,
                cloud_service_product_level: levelBeforeTypeCast,
              };
              break;
            }
  
            case "server_operating_system": {
              if (!recordId) break;
              const levelBeforeTypeCast = levelBeforeTypeCastFromLevelValue(levelValue);
              attributes.skill_sheet_server_operating_system_infos_attributes[String(recordId)] =
                {
                  id: recordId,
                  server_operating_system_level: levelBeforeTypeCast,
                  server_environment_level: levelBeforeTypeCast,
                };
              break;
            }
  
            case "middleware": {
              if (!recordId) break;
              const levelBeforeTypeCast = levelBeforeTypeCastFromLevelValue(levelValue);
              attributes.skill_sheet_middleware_infos_attributes[String(recordId)] = {
                id: recordId,
                level: levelBeforeTypeCast,
                middleware_product_level: levelBeforeTypeCast,
              };
              break;
            }
  
          case "operating_system":
            setAttributes(
              "skill_sheet_operating_system_infos_attributes",
              recordId,
              "level",
              levelValue
            );
            break;
  
          case "db":
            setAttributes(
              "skill_sheet_db_infos_attributes",
              recordId,
              "level",
              levelValue
            );
            break;
  
          case "network":
            setAttributes(
              "skill_sheet_network_infos_attributes",
              recordId,
              "level",
              levelValue
            );
            break;
  
          case "design_tool":
            setAttributes(
              "skill_sheet_design_tool_infos_attributes",
              recordId,
              "level",
              levelValue
            );
            break;
  
          default:
            break;
        }
      }
    }
  
    return attributes;
  }

  function upsertLanguageInfoAttributes(attributes, recordId, partial) {
    if (!recordId) return;
  
    const key = String(recordId);
  
    attributes.skill_sheet_language_infos_attributes[key] = {
      ...(attributes.skill_sheet_language_infos_attributes[key] || {}),
      id: recordId,
      ...partial,
    };
  }

  // 保存
  async function handleSave() {
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
  
      const skillSheetTemplateAttributes =
        buildSkillSheetTemplateAttributesFromSkillCategories(skillCategories);
  
      const response = await fetch("/skill_sheet_self_evaluations", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({
          skill_sheet_template: skillSheetTemplateAttributes,
        }),
        credentials: "same-origin",
      });
  
      if (!response.ok) {
        const responseBody = await response.json().catch(() => null);
  
        if (responseBody?.errors?.length) {
          responseBody.errors.forEach((message) => toast.error(message));
        } else {
          toast.error(`保存に失敗しました（${response.status}）`);
        }
  
        console.error("save failed", response.status, responseBody);
        return;
      }
  
      toast.success("保存しました");
      window.location.assign("/skill_sheets");
    } catch (error) {
      console.error("save error", error);
      toast.error("通信エラーで保存に失敗しました");
    }
  }

  // カラムの比率
const categoryEvaluationGridColumns = {
  xs: "1fr",
  sm: "minmax(200px, 0.35fr) minmax(260px, 0.65fr)",
};

  return (
    <Box onSubmitCapture={(event) => event.preventDefault()}>

      <FrameCard sx={{ p: { xs: 2, sm: 8 } }}>
        <Typography sx={{ fontSize: 24, fontWeight: "bold", mb: 4 }}>
          自己評価編集
        </Typography>

        <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: "8px" }}>
          評価基準
        </Typography>
        <EvaluationStandardsSection />

        {isMobile ? (
          <Box
            sx={{
              mt: 7,
              mb: 2,
              py: "12px",
              bgcolor: "#E9EAFA",
            }}
          >
            <Typography sx={{ display: "grid", fontSize: "12px" }}>自己評価</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: categoryEvaluationGridColumns,
              mt: 7,
              mb: 4,
              py: "12px",
              bgcolor: "#E9EAFA",
            }}
          >
            <Typography sx={{ display: "grid", pl: 4, fontSize: "12px" }}>カテゴリー</Typography>
            <Typography sx={{ display: "grid", fontSize: "12px" }}>自己評価</Typography>
          </Box>
        )}

        {skillCategories.map((category, categoryIndex) => (
          <React.Fragment key={category.categoryKey}>
            {categoryIndex !== 0 && <Divider sx={{ my: 4 }} />}
            <Box
              key={category.categoryLabel}
              sx={{
                display: "grid",
                gridTemplateColumns: categoryEvaluationGridColumns,
                columnGap: { xs: 0, sm: 3 },
                rowGap: { xs: 2, sm: 0 },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "center" },
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  {category.categoryLabel}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
                {category.skillGroups.map((group) => (
                  <SkillCard
                    key={group.groupId}
                    parentSkill={group.parentSkill}
                    childSkills={group.childSkills}
                    onParentLevelChange={(value) =>
                      handleParentLevelChange(
                        category.categoryKey,
                        group.groupId,
                        value
                      )
                    }
                    onChildLevelChange={(recordId, value) =>
                      handleChildLevelChange(
                        category.categoryKey,
                        group.groupId,
                        recordId,
                        value
                      )
                    }
                  />
                ))}
              </Box>
            </Box>
          </React.Fragment>
        ))}
        <Box
          sx={{
            mt: { xs: 5, sm: 10 },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ActionButton
            label="保存する"
            sx={{
              backgroundColor: PROJECT_COLOR,
              "&:hover": { backgroundColor: PROJECT_COLOR_DARK },
            }}
            onClick={(event) => {
              event?.preventDefault?.();
              event?.stopPropagation?.();
              handleSave();
            }}
          />
        </Box>
      </FrameCard>
    </Box>
  );
}